import { motion } from "framer-motion";
import { Check, Star, Zap, Building, Users, BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";

export const PricingSection: React.FC = () => {
  const { t, i18n } = useTranslation();

  const pricingPlans = [
    {
      name: t('pricing.plans.individual.name'),
      title: "3wChat",
      price: "0",
      period: t('pricing.plans.individual.period'),
      description: t('pricing.plans.individual.description'),
      icon: Star,
      color: 'from-blue-500 to-purple-500',
      popular: false,
      features: [
        t('pricing.plans.individual.features.webNavigation'),
        t('pricing.plans.individual.features.voiceInput'),
        t('pricing.plans.individual.features.voiceOutput'),
        t('pricing.plans.individual.features.siteInteraction'),
        t('pricing.plans.individual.features.reportGeneration'),
        t('pricing.plans.individual.features.voiceConversation'),
        t('pricing.plans.individual.features.toggleControl')
      ]
    },
    {
      name: t('pricing.plans.business.name'),
      title: "3wChat Business",
      price: "99",
      period: t('pricing.plans.business.period'),
      description: t('pricing.plans.business.description'),
      icon: Building,
      color: 'from-purple-500 to-pink-500',
      popular: true,
      features: [
        t('pricing.plans.business.features.allIndividual'),
        t('pricing.plans.business.features.visitHistory'),
        t('pricing.plans.business.features.conversationHistory'),
        t('pricing.plans.business.features.userSummaries'),
        t('pricing.plans.business.features.decisionSupport'),
        t('pricing.plans.business.features.actionPlans'),
        t('pricing.plans.business.features.conversationalAgent'),
        t('pricing.plans.business.features.analytics')
      ]
    },
    {
      name: t('pricing.plans.enterprise.name'),
      title: "3wChat Enterprise",
      price: t('pricing.plans.enterprise.price'),
      period: t('pricing.plans.enterprise.period'),
      description: t('pricing.plans.enterprise.description'),
      icon: Users,
      color: 'from-green-500 to-teal-500',
      popular: false,
      features: [
        t('pricing.plans.enterprise.features.allBusiness'),
        t('pricing.plans.enterprise.features.unlimitedUsers'),
        t('pricing.plans.enterprise.features.customIntegration'),
        t('pricing.plans.enterprise.features.prioritySupport'),
        t('pricing.plans.enterprise.features.advancedAnalytics'),
        t('pricing.plans.enterprise.features.whiteLabel'),
        t('pricing.plans.enterprise.features.sla'),
        t('pricing.plans.enterprise.features.onPremise')
      ]
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {t('pricing.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className={`relative bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all ${
                plan.popular ? 'ring-4 ring-purple-500 ring-opacity-50 scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    {t('pricing.mostPopular')}
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <div className={`w-20 h-20 bg-gradient-to-r ${plan.color} rounded-3xl flex items-center justify-center mx-auto mb-6`}>
                  <plan.icon className="w-10 h-10 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {plan.description}
                </p>

                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-900 dark:text-white">
                    {plan.price === t('pricing.plans.enterprise.price') ? '' : '$'}{plan.price}
                  </span>
                  {plan.price !== t('pricing.plans.enterprise.price') && (
                    <span className="text-gray-600 dark:text-gray-300 text-lg">
                      /{plan.period}
                    </span>
                  )}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-4 px-6 rounded-2xl font-semibold text-lg transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                      : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                  }`}
                >
                  {plan.price === '0' ? t('pricing.getStarted') : 
                   plan.price === t('pricing.plans.enterprise.price') ? t('pricing.contactSales') : 
                   t('pricing.startTrial')}
                </motion.button>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 dark:text-white text-lg mb-4">
                  {t('pricing.features')}
                </h4>
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                    </div>
                    <span className="text-gray-600 dark:text-gray-300">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg">
            <BarChart3 className="w-12 h-12 text-purple-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {t('pricing.guarantee.title')}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {t('pricing.guarantee.description')}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};